import { useState } from 'react';
import { useNavigate } from 'react-router';
import clsx from 'clsx';

import { Button, Card, Icon } from '@lib/ui';
import { Input, Textarea, Select } from '@lib/ui';
import type { PipelineSource, PipelineInput, PipelineStep } from '../types';
import { defaultPipelineSource, LEGACY_SCENARIOS } from '../types';
import { SourcePreview } from '../components/SourcePreview';

type StepType = 'web_fetch' | 'web_discover' | 'llm';

export default function PipelineWizard() {
  const navigate = useNavigate();
  const [source, setSource] = useState<PipelineSource>(defaultPipelineSource);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);

  const updateSource = (updates: Partial<PipelineSource>) => {
    setSource((prev) => ({ ...prev, ...updates }));
  };

  const addInput = () => {
    updateSource({
      inputs: [...source.inputs, { name: '', type: 'string', required: true }],
    });
  };

  const updateInput = (index: number, updates: Partial<PipelineInput>) => {
    const newInputs = [...source.inputs];
    newInputs[index] = { ...newInputs[index], ...updates };
    updateSource({ inputs: newInputs });
  };

  const removeInput = (index: number) => {
    updateSource({ inputs: source.inputs.filter((_, i) => i !== index) });
  };

  const addStep = (type: StepType) => {
    let step: PipelineStep;
    switch (type) {
      case 'web_fetch':
        step = { type: 'web_fetch', url: '' };
        break;
      case 'web_discover':
        step = { type: 'web_discover', baseUrl: '' };
        break;
      case 'llm':
        step = { type: 'llm', prompt: '' };
        break;
    }
    updateSource({ steps: [...source.steps, step] });
  };

  const updateStep = (index: number, updates: Partial<PipelineStep>) => {
    const newSteps = [...source.steps];
    newSteps[index] = { ...newSteps[index], ...updates } as PipelineStep;
    updateSource({ steps: newSteps });
  };

  const removeStep = (index: number) => {
    updateSource({ steps: source.steps.filter((_, i) => i !== index) });
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newSteps = [...source.steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    updateSource({ steps: newSteps });
  };

  const loadScenario = (scenarioId: string) => {
    setActiveScenario(scenarioId);
    switch (scenarioId) {
      case 'pure-llm':
        setSource({
          title: 'Daily Motivation',
          inputs: [],
          steps: [{ type: 'llm', prompt: 'Write a motivational quote about perseverance' }],
        });
        break;
      case 'url-summary':
        setSource({
          title: 'Article Summarizer',
          inputs: [{ name: 'url', type: 'url', required: true }],
          steps: [
            { type: 'web_fetch', url: '{{inputs.url}}' },
            {
              type: 'llm',
              prompt: `{{steps.web_fetch.content}}

Summarize the main points in 3 bullet points.`,
            },
          ],
        });
        break;
      case 'site-discovery':
        setSource({
          title: 'Site Pricing Extractor',
          inputs: [{ name: 'site', type: 'url', required: true }],
          steps: [
            { type: 'web_discover', baseUrl: '{{inputs.site}}', hint: 'pricing' },
            {
              type: 'llm',
              prompt: `{{steps.web_discover.pages}}

Extract all pricing tiers with their features.`,
            },
          ],
        });
        break;
      case 'complex-extraction':
        setSource({
          title: 'Amazon Product Reviews',
          inputs: [],
          steps: [
            { type: 'web_fetch', url: 'https://amazon.com/product/123' },
            {
              type: 'llm',
              prompt: `{{steps.web_fetch.content}}

Extract all user comments. For each:
- Username
- Rating (1-5)
- Date
- Comment text

Then summarize overall sentiment.`,
            },
          ],
        });
        break;
    }
  };

  const getStepIcon = (type: StepType) => {
    switch (type) {
      case 'web_fetch':
        return 'globe';
      case 'web_discover':
        return 'search';
      case 'llm':
        return 'sparkles';
    }
  };

  const getStepColor = (type: StepType) => {
    switch (type) {
      case 'web_fetch':
        return 'bg-blue-500';
      case 'web_discover':
        return 'bg-amber-500';
      case 'llm':
        return 'bg-purple-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-purple-500 w-10 h-10 rounded-lg flex items-center justify-center">
              <Icon name="folder" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Pipeline Model
              </h1>
              <p className="text-sm text-gray-500 dark:text-dark-400">Step-based execution flow</p>
            </div>
          </div>
          <Button variant="flat" onClick={() => navigate('/poc/source-modeling')}>
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Scenario selector */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Load a scenario:
          </p>
          <div className="flex flex-wrap gap-2">
            {LEGACY_SCENARIOS.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => loadScenario(scenario.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeScenario === scenario.id
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-dark-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                )}
              >
                {scenario.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card skin="shadow" className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Configure Source
            </h2>

            <div className="space-y-6">
              {/* Title */}
              <Input
                label="Title"
                placeholder="e.g., Article Summarizer"
                value={source.title}
                onChange={(e) => updateSource({ title: e.target.value })}
              />

              {/* Inputs */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-fg">Dynamic Inputs</label>
                  <Button size="sm" variant="soft" onClick={addInput}>
                    <Icon name="plus" size="sm" />
                    <span className="ml-1">Add Input</span>
                  </Button>
                </div>
                {source.inputs.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                    No dynamic inputs.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {source.inputs.map((input, index) => (
                      <div
                        key={index}
                        className="p-3 rounded-lg border border-gray-200 dark:border-dark-600"
                      >
                        <div className="flex items-start gap-3">
                          <Input
                            placeholder="Input name"
                            value={input.name}
                            onChange={(e) => updateInput(index, { name: e.target.value })}
                            classNames={{ root: 'flex-1' }}
                          />
                          <Select
                            value={input.type}
                            onChange={(e) =>
                              updateInput(index, { type: e.target.value as PipelineInput['type'] })
                            }
                            data={['string', 'url', 'text']}
                            classNames={{ root: 'w-28' }}
                          />
                          <Button
                            isIcon
                            variant="flat"
                            color="error"
                            size="sm"
                            onClick={() => removeInput(index)}
                          >
                            <Icon name="x" size="sm" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Steps */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-fg">Pipeline Steps</label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="soft" onClick={() => addStep('web_fetch')}>
                      <Icon name="globe" size="sm" />
                      <span className="ml-1">Fetch</span>
                    </Button>
                    <Button size="sm" variant="soft" onClick={() => addStep('web_discover')}>
                      <Icon name="search" size="sm" />
                      <span className="ml-1">Discover</span>
                    </Button>
                    <Button size="sm" variant="soft" onClick={() => addStep('llm')}>
                      <Icon name="sparkles" size="sm" />
                      <span className="ml-1">LLM</span>
                    </Button>
                  </div>
                </div>

                {source.steps.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                    No steps. Add steps to build the pipeline.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {source.steps.map((step, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg border border-gray-200 dark:border-dark-600"
                      >
                        {/* Step header */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 dark:text-dark-500 w-5">
                              {index + 1}
                            </span>
                            <div
                              className={clsx(
                                'w-6 h-6 rounded flex items-center justify-center',
                                getStepColor(step.type)
                              )}
                            >
                              <Icon
                                name={getStepIcon(step.type)}
                                size="sm"
                                className="text-white"
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {step.type === 'web_fetch'
                                ? 'Web Fetch'
                                : step.type === 'web_discover'
                                  ? 'Web Discover'
                                  : 'LLM'}
                            </span>
                          </div>
                          <div className="flex-1" />
                          <div className="flex items-center gap-1">
                            <Button
                              isIcon
                              variant="flat"
                              size="sm"
                              onClick={() => moveStep(index, 'up')}
                              disabled={index === 0}
                            >
                              <Icon name="chevron-up" size="sm" />
                            </Button>
                            <Button
                              isIcon
                              variant="flat"
                              size="sm"
                              onClick={() => moveStep(index, 'down')}
                              disabled={index === source.steps.length - 1}
                            >
                              <Icon name="chevron-down" size="sm" />
                            </Button>
                            <Button
                              isIcon
                              variant="flat"
                              color="error"
                              size="sm"
                              onClick={() => removeStep(index)}
                            >
                              <Icon name="x" size="sm" />
                            </Button>
                          </div>
                        </div>

                        {/* Step config */}
                        {step.type === 'web_fetch' && (
                          <Input
                            label="URL"
                            placeholder="https://... or {{inputs.url}}"
                            value={step.url}
                            onChange={(e) => updateStep(index, { url: e.target.value })}
                            description="Reference as {{steps.web_fetch.content}} in later steps"
                          />
                        )}
                        {step.type === 'web_discover' && (
                          <div className="space-y-3">
                            <Input
                              label="Base URL"
                              placeholder="https://... or {{inputs.site}}"
                              value={step.baseUrl}
                              onChange={(e) => updateStep(index, { baseUrl: e.target.value })}
                            />
                            <Input
                              label="Hint (optional)"
                              placeholder="e.g., pricing"
                              value={step.hint || ''}
                              onChange={(e) => updateStep(index, { hint: e.target.value })}
                              description="Reference as {{steps.web_discover.pages}} in later steps"
                            />
                          </div>
                        )}
                        {step.type === 'llm' && (
                          <Textarea
                            label="Prompt"
                            placeholder={`Use {{steps.step_type.content}} to reference previous step output.
Example:
{{steps.web_fetch.content}}

Summarize the main points.`}
                            rows={4}
                            value={step.prompt}
                            onChange={(e) => updateStep(index, { prompt: e.target.value })}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Preview */}
          <div className="space-y-6">
            <SourcePreview title="Data Structure" data={source} format="json" />
            <SourcePreview title="User Config (YAML)" data={source} format="yaml" />

            {/* Visual Pipeline */}
            {source.steps.length > 0 && (
              <Card skin="shadow" className="p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Execution Flow
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {source.inputs.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-dark-700 text-sm font-medium text-gray-700 dark:text-dark-200">
                        Inputs
                      </div>
                      <Icon name="arrow-right" size="sm" className="text-gray-400" />
                    </>
                  )}
                  {source.steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-sm font-medium text-white',
                          getStepColor(step.type)
                        )}
                      >
                        {step.type === 'llm'
                          ? 'LLM'
                          : step.type === 'web_fetch'
                            ? 'Fetch'
                            : 'Discover'}
                      </div>
                      {index < source.steps.length - 1 && (
                        <Icon name="arrow-right" size="sm" className="text-gray-400" />
                      )}
                    </div>
                  ))}
                  <Icon name="arrow-right" size="sm" className="text-gray-400" />
                  <div className="px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-sm font-medium text-green-700 dark:text-green-300">
                    Output
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
